/**
 * The frame every screen sits in: who you are, which channel, and the way out.
 *
 * The channel is named in the header on every screen. On a multi-tenant admin
 * tool, "which tenant am I editing" is the one question that must never need
 * asking — the predecessors' console showed every channel's content at once
 * and the answer was "all of them, silently".
 */
import type { ReactNode } from 'react';
import { useFeature, useSession } from '../Session.jsx';
import { Link } from './primitives.jsx';
import { CATALOG, CATALOG_KINDS } from '../lib/tomo.js';
import { SiteFrame } from './SiteFrame.jsx';
import { hrefFor } from '../lib/router.js';
import { useRoute } from '../useRoute.js';

export function Shell(
  // Explicitly `| undefined`: exactOptionalPropertyTypes makes "absent" and
  // "present and undefined" different types, and a caller with an optional
  // channel produces the second.
  { channelId, children }: { channelId?: string | undefined; children: ReactNode }
): ReactNode {
  const session = useSession();
  const collectibles = useFeature('collectibles');
  const route = useRoute();
  const channel = channelId
    ? session.channels.find((c) => c.id === channelId)
    : undefined;

  /*
   * THE SITE'S FRAME, not a studio frame of its own. The studio used to have
   * its own bar, its own width and a text "Sign out" where every other page
   * has your face; now the only thing that says you are in the studio is the
   * trail, which is the thing that should.
   */
  const viewer = {
    uid: session.user.uid,
    handle: session.user.handle,
    displayName: session.user.display_name,
    avatarUrl: session.user.avatar_url,
  };
  const trail = [
    { label: 'studio', ...(channel ? { href: hrefFor([]) } : {}) },
    ...(channel ? [{ label: channel.display_name }] : []),
  ];

  return (
    <SiteFrame
      viewer={viewer}
      trail={trail}
      actions={
        <span className="flex items-center gap-4 text-sm text-ink-400">
          {channel ? (
            <span className="rounded-full border border-ink-700 px-2 py-0.5 text-xs text-ink-400">
              {channel.role}
            </span>
          ) : null}
          {collectibles ? (
            <>
              <Link href="/collection" className="hover:text-ink-200">Collection</Link>
              <Link href="/trades" className="hover:text-ink-200">Trades</Link>
            </>
          ) : null}
        </span>
      }
      below={
        /* Every tab is part of collectibles, so without the flag there is
           no navigation to draw — the channel home is the whole studio. */
        channel && collectibles ? (
          <nav className="border-b border-ink-800">
            <ul className="mx-auto flex max-w-5xl gap-1 px-4">
              {/* Always offered: every role collects, including the owner. */}
              <NavTab
                href={hrefFor([channel.id, 'collection'])}
                active={route.segments[1] === 'collection'}
              >
                Collection
              </NavTab>
              <NavTab
                href={hrefFor([channel.id, 'files'])}
                active={route.segments[1] === 'files'}
              >
                Files
              </NavTab>
              <NavTab
                href={hrefFor([channel.id, 'items'])}
                active={route.segments[1] === 'items'}
              >
                Collectibles
              </NavTab>
              <NavTab
                href={hrefFor([channel.id, 'rarities'])}
                active={route.segments[1] === 'rarities'}
              >
                Rarities
              </NavTab>
              {CATALOG_KINDS
                // Offered only where the role can actually open it. Hiding a
                // tab is a courtesy, never a control — the server checks the
                // scope again on every request.
                .filter((kind) => channel.scopes.includes(CATALOG[kind].reads))
                .map((kind) => (
                <NavTab
                  key={kind}
                  href={hrefFor([channel.id, kind])}
                  active={route.segments[1] === kind}
                >
                  {CATALOG[kind].label}
                </NavTab>
              ))}
            </ul>
          </nav>
        ) : null
      }
    >
      {children}
    </SiteFrame>
  );
}

function NavTab(
  { href, active, children }: { href: string; active: boolean; children: ReactNode }
): ReactNode {
  return (
    <li>
      <Link
        href={href}
        className={`-mb-px inline-block border-b-2 px-3 py-2 text-sm ${
          active
            ? 'border-brand-400 text-ink-50'
            : 'border-transparent text-ink-400 hover:text-ink-200'
        }`}
      >
        {children}
      </Link>
    </li>
  );
}
